import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Check, X, ChevronUp, ChevronDown, Brain, FileText } from 'lucide-react';

const QuizBuilder = ({ content, onChange, gradingType = 'auto', onContentChange }) => {
    const [quiz, setQuiz] = useState({
        questions: []
    });

    const isManualGrading = gradingType === 'manual';

    useEffect(() => {
        try {
            if (content) {
                // Handle both string (from database) and object (from AI generation)
                if (typeof content === 'string' && content.trim()) {
                    const parsed = JSON.parse(content);
                    setQuiz(parsed);
                } else if (typeof content === 'object' && content !== null) {
                    // Content is already an object (from AI generation)
                    setQuiz(content);
                }
            }
        } catch (e) {
            // If content is not valid JSON, initialize empty
            setQuiz({ questions: [] });
        }
    }, [content]);

    const handleQuizChange = (updatedQuiz) => {
        setQuiz(updatedQuiz);
        onChange(JSON.stringify(updatedQuiz, null, 2));
    };

    const addQuestion = () => {
        const newQuestion = isManualGrading ? {
            id: Date.now(),
            question: '',
            answerGuideline: '', // For admin reference on what a good answer looks like
            points: 1
        } : {
            id: Date.now(),
            question: '',
            options: ['', '', '', ''],
            correctAnswer: 0,
            explanation: '',
            points: 1
        };

        handleQuizChange({
            ...quiz,
            questions: [...quiz.questions, newQuestion]
        });
        onContentChange?.('quiz_questions', `Added new question ${quiz.questions.length + 1}`);
    };

    const updateQuestion = (index, field, value) => {
        const updatedQuestions = [...quiz.questions];
        updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };

        const updated = { ...quiz, questions: updatedQuestions };
        handleQuizChange(updated);

        const fieldName = field === 'question' ? 'question text' : field === 'correctAnswer' ? 'correct answer' : field;

        let valueDisplay = value;
        if (field === 'correctAnswer') {
            const currentQuestionIdx = index;
            const selectedOptionIdx = parseInt(value);
            // Use updatedQuestions to ensure we get the option text from the latest state (though it shouldn't have changed in this op)
            const optionText = updatedQuestions[currentQuestionIdx]?.options?.[selectedOptionIdx] || '';
            valueDisplay = `Option ${selectedOptionIdx + 1}${optionText ? ` ("${optionText}")` : ''}`;
        } else if (typeof value === 'string' && value.length > 50) {
            valueDisplay = value.substring(0, 50) + '...';
        }

        onContentChange?.('quiz_questions', `Updated ${fieldName} for question ${index + 1} to: ${valueDisplay}`);
    };

    const updateOption = (questionIndex, optionIndex, value) => {
        const updatedQuestions = [...quiz.questions];
        const updatedOptions = [...updatedQuestions[questionIndex].options];
        updatedOptions[optionIndex] = value;
        updatedQuestions[questionIndex] = { ...updatedQuestions[questionIndex], options: updatedOptions };

        handleQuizChange({ ...quiz, questions: updatedQuestions });

        const valueDisplay = value.length > 50 ? value.substring(0, 50) + '...' : value;
        const isCorrectAnswer = updatedQuestions[questionIndex].correctAnswer === optionIndex;
        let logMessage = `Updated option ${String.fromCharCode(65 + optionIndex)} for question ${questionIndex + 1} to: "${valueDisplay}"`;

        if (isCorrectAnswer) {
            logMessage += ` (This is currently the CORRECT ANSWER)`;
        }

        onContentChange?.('quiz_options', logMessage);
    };

    const addOption = (questionIndex) => {
        const updatedQuestions = [...quiz.questions];
        const updatedOptions = [...updatedQuestions[questionIndex].options, ''];
        updatedQuestions[questionIndex] = { ...updatedQuestions[questionIndex], options: updatedOptions };

        handleQuizChange({ ...quiz, questions: updatedQuestions });
    };

    const removeOption = (questionIndex, optionIndex) => {
        if (quiz.questions[questionIndex].options.length > 2) {
            const updatedQuestions = [...quiz.questions];
            const updatedOptions = updatedQuestions[questionIndex].options.filter((_, idx) => idx !== optionIndex);

            let newCorrectAnswer = updatedQuestions[questionIndex].correctAnswer;
            if (newCorrectAnswer === optionIndex) {
                newCorrectAnswer = 0;
            } else if (newCorrectAnswer > optionIndex) {
                newCorrectAnswer--;
            }

            updatedQuestions[questionIndex] = {
                ...updatedQuestions[questionIndex],
                options: updatedOptions,
                correctAnswer: newCorrectAnswer
            };

            handleQuizChange({ ...quiz, questions: updatedQuestions });
        }
    };

    const deleteQuestion = (index) => {
        const updatedQuestions = quiz.questions.filter((_, idx) => idx !== index);
        handleQuizChange({ ...quiz, questions: updatedQuestions });
        onContentChange?.('quiz_questions', `Deleted question ${index + 1}`);
    };

    const moveQuestion = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex >= 0 && newIndex < quiz.questions.length) {
            const updatedQuestions = [...quiz.questions];
            [updatedQuestions[index], updatedQuestions[newIndex]] =
                [updatedQuestions[newIndex], updatedQuestions[index]];

            handleQuizChange({ ...quiz, questions: updatedQuestions });
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Quiz Builder</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${isManualGrading
                        ? 'bg-purple-100 text-purple-700 dark:text-purple-400'
                        : 'bg-green-100 text-green-700 dark:text-green-400'
                        }`}>
                        {isManualGrading ? 'Manual Grading' : 'Auto-Graded'}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={addQuestion}
                    className="flex items-center gap-1.5 bg-primary hover:bg-primary-hover text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap shrink-0"
                >
                    <Plus className="w-4 h-4" />
                    Add Question
                </button>
            </div>

            {/* Info banner based on grading type */}
            <div className={`rounded-lg p-3 text-sm ${isManualGrading
                ? 'bg-purple-50 dark:bg-purple-500/10 border border-purple-200 text-purple-800'
                : 'bg-green-50 dark:bg-green-500/10 border border-green-200 text-green-800'
                }`}>
                {isManualGrading ? (
                    <div className="flex items-start gap-2">
                        <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <p>
                            <strong>Manual Grading Mode:</strong> Create open-ended questions.
                            Employees will provide written answers, and an admin will review and grade them.
                        </p>
                    </div>
                ) : (
                    <div className="flex items-start gap-2">
                        <Brain className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <p>
                            <strong>Auto-Graded Mode:</strong> Create multiple choice questions.
                            The system will automatically grade based on correct answers you set.
                        </p>
                    </div>
                )}
            </div>

            {quiz.questions.length === 0 ? (
                <div className="bg-gray-50 dark:bg-slate-800/50 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-8 text-center">
                    <p className="text-gray-600 dark:text-slate-300 mb-4">No questions yet. Click "Add Question" to get started.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {quiz.questions.map((question, qIndex) => (
                        <div key={question.id || qIndex} className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg p-3 shadow-sm">
                            {/* Question Header */}
                            <div className="flex items-start gap-1.5 mb-2">
                                <div className="flex flex-col gap-0 mt-1">
                                    <button
                                        type="button"
                                        onClick={() => moveQuestion(qIndex, 'up')}
                                        disabled={qIndex === 0}
                                        className="p-0.5 text-gray-400 hover:text-gray-600 dark:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <ChevronUp className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => moveQuestion(qIndex, 'down')}
                                        disabled={qIndex === quiz.questions.length - 1}
                                        className="p-0.5 text-gray-400 hover:text-gray-600 dark:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <ChevronDown className="w-3.5 h-3.5" />
                                    </button>
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                        <span className="text-sm font-semibold text-gray-600 dark:text-slate-300 whitespace-nowrap">Question {qIndex + 1}</span>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number"
                                                value={question.points || 1}
                                                onChange={(e) => updateQuestion(qIndex, 'points', parseInt(e.target.value) || 1)}
                                                min="1"
                                                className="w-16 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-sm shrink-0"
                                                placeholder="Points"
                                            />
                                            <span className="text-xs text-gray-500 dark:text-slate-400 whitespace-nowrap">points</span>
                                        </div>
                                    </div>

                                    <textarea
                                        value={question.question}
                                        onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                                        placeholder="Enter your question here..."
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                        rows="2"
                                    />
                                </div>

                                <button
                                    type="button"
                                    onClick={() => deleteQuestion(qIndex)}
                                    className="p-2 text-red-600 hover:bg-red-50 dark:bg-red-500/10 rounded-lg transition-colors"
                                    title="Delete Question"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Answer Section - Different based on grading type */}
                            {isManualGrading ? (
                                /* Manual Grading: Answer Guidelines for Admin */
                                <div className="ml-8">
                                    <label className="text-sm font-medium text-gray-700 dark:text-slate-200 mb-1 block">
                                        Answer Guideline (for grading reference):
                                    </label>
                                    <textarea
                                        value={question.answerGuideline || ''}
                                        onChange={(e) => updateQuestion(qIndex, 'answerGuideline', e.target.value)}
                                        placeholder="Describe what a good answer should include. This helps admins grade consistently..."
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                        rows="3"
                                    />
                                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                                        This guideline is only visible to admins when grading, not to employees.
                                    </p>
                                </div>
                            ) : (
                                /* Auto-Graded: Multiple Choice Options */
                                <>
                                    <div className="space-y-2 mb-3">
                                        <label className="text-sm font-medium text-gray-700 dark:text-slate-200">Answer Options:</label>
                                        {question.options?.map((option, oIndex) => (
                                            <div key={oIndex} className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    name={`correct-${qIndex}`}
                                                    checked={question.correctAnswer === oIndex}
                                                    onChange={() => updateQuestion(qIndex, 'correctAnswer', oIndex)}
                                                    className="w-4 h-4 text-green-600 shrink-0"
                                                />
                                                <input
                                                    type="text"
                                                    value={option}
                                                    onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                                                    placeholder={`Option ${String.fromCharCode(65 + oIndex)}`}
                                                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                                />
                                                {question.correctAnswer === oIndex && (
                                                    <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                                                )}
                                                {question.options.length > 2 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeOption(qIndex, oIndex)}
                                                        className="p-2 text-red-600 hover:bg-red-50 dark:bg-red-500/10 rounded-lg transition-colors"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => addOption(qIndex)}
                                            className="text-sm text-primary hover:text-primary-hover font-medium"
                                        >
                                            + Add Option
                                        </button>
                                    </div>

                                    {/* Explanation */}
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 dark:text-slate-200 mb-1 block">
                                            Explanation (optional):
                                        </label>
                                        <textarea
                                            value={question.explanation || ''}
                                            onChange={(e) => updateQuestion(qIndex, 'explanation', e.target.value)}
                                            placeholder="Explain why this answer is correct..."
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                            rows="2"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Quiz Summary */}
            {quiz.questions.length > 0 && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                    <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-primary">
                            Total Questions: {quiz.questions.length}
                        </span>
                        <span className="font-medium text-primary">
                            Total Points: {quiz.questions.reduce((sum, q) => sum + (q.points || 1), 0)}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuizBuilder;
